export interface ClientCaseStudy {
  title: string;
  category: string;
  description: string;
  image: string;
  overview: string;
  challenge: string;
  solution: string;
  outcome: string;
  tags: string[];
  stats: Array<{
    value: string;
    label: string;
  }>;
}

export const CLIENT_CASE_STUDIES = {
  liverpool: {
    title: 'Liverpool',
    category: 'Digital Fidelity Strategy',
    description: 'Estrategia digital para fidelización, comunicación y crecimiento comercial.',
    image: 'assets/images/projects/portfolio/liverpool.jpg',
    overview:
      'Acompañamos una iniciativa digital enfocada en mantener presencia, mejorar comunicación y reforzar la relación con audiencias de alto valor.',
    challenge:
      'Ordenar mensajes, canales y puntos de contacto para que la estrategia de fidelización se sintiera consistente y accionable.',
    solution:
      'Estructuramos una narrativa digital clara, criterios de comunicación y una ruta de activación orientada a engagement y valor comercial.',
    outcome:
      'Una base estratégica más clara para sostener campañas, conectar con clientes y medir oportunidades de crecimiento.',
    tags: ['Fidelización', 'Estrategia digital', 'Retail', 'Comunicación'],
    stats: [
      { value: '360', label: 'visión de canales' },
      { value: '4', label: 'frentes estratégicos' },
      { value: '1', label: 'sistema de comunicación' },
    ],
  },
  carajillo: {
    title: 'Carajillo',
    category: 'Marketing Strategy',
    description: 'Estrategia de marketing para reforzar su presencia como restaurante y activar intención de visita.',
    image: 'assets/images/projects/portfolio/carajillo.jpg',
    overview:
      'Ordenamos la estrategia de marketing de Carajillo para comunicar su experiencia gastronómica, fortalecer presencia y convertir atención en intención de visita.',
    challenge:
      'Traducir ambiente, menú y experiencia en mensajes claros que ayudaran a despertar deseo, recordación y tráfico hacia el restaurante.',
    solution:
      'Definimos una ruta de comunicación y campañas enfocada en destacar la experiencia gastronómica, ordenar contenidos y priorizar mensajes comerciales.',
    outcome:
      'Una base de marketing más clara para sostener presencia digital, activar audiencias y reforzar a Carajillo como destino gastronómico.',
    tags: ['Restaurante', 'Marketing', 'Gastronomía', 'Brand experience'],
    stats: [
      { value: '3', label: 'ejes de campaña' },
      { value: '+', label: 'presencia digital' },
      { value: '1', label: 'plataforma de marketing' },
    ],
  },
  citibanamex: {
    title: 'Citibanamex',
    category: 'Retail Marketing Strategy',
    description: 'Estrategia retail para campañas, comunicación y activaciones orientadas a negocio.',
    image: 'assets/images/projects/portfolio/citibanamex.jpg',
    overview:
      'Diseñamos una aproximación retail para ordenar mensajes, momentos y activaciones con foco en claridad comercial.',
    challenge:
      'Traducir objetivos de negocio en comunicación clara, relevante y adaptable a distintos puntos de contacto.',
    solution:
      'Creamos una estructura estratégica para campañas y activaciones, conectando audiencia, mensaje y objetivo comercial.',
    outcome:
      'Un marco de comunicación más accionable para impulsar presencia, consistencia y lectura de resultados.',
    tags: ['Retail', 'Campañas', 'Activación', 'Estrategia'],
    stats: [
      { value: 'B2C', label: 'enfoque comercial' },
      { value: '3', label: 'capas de mensaje' },
      { value: 'ROI', label: 'lectura de negocio' },
    ],
  },
  pernodRicard: {
    title: 'Pernod Ricard',
    category: 'Marketing Strategy',
    description: 'Presencia y conexión comercial para marcas de consumo.',
    image: 'assets/images/projects/portfolio/pernod-ricard.png',
    overview:
      'Colaboramos en una estrategia de marketing para fortalecer presencia de marca y conectar mejor con consumidores.',
    challenge:
      'Mantener consistencia entre marca, comunicación y activaciones en un entorno de alta competencia por atención.',
    solution:
      'Ordenamos objetivos, mensajes y puntos de contacto para apoyar una presencia comercial más coherente.',
    outcome:
      'Una base estratégica útil para campañas con mejor dirección, más claridad y mayor consistencia de marca.',
    tags: ['Consumo', 'Brand strategy', 'Marketing', 'Activación'],
    stats: [
      { value: '1', label: 'narrativa central' },
      { value: '3', label: 'momentos de contacto' },
      { value: '+', label: 'consistencia de marca' },
    ],
  },
  jaloma: {
    title: 'Jaloma',
    category: 'Retail Marketing Strategy',
    description: 'Visibilidad, canales y comunicación de producto para retail.',
    image: 'assets/images/projects/portfolio/jaloma.png',
    overview:
      'Impulsamos una mirada retail para comunicar productos con mayor claridad y reforzar presencia en canales comerciales.',
    challenge:
      'Hacer que la oferta de producto fuera más fácil de entender, priorizar y activar en comunicación comercial.',
    solution:
      'Definimos una estructura de mensajes y prioridades para mejorar visibilidad, orden y lectura de valor.',
    outcome:
      'Una comunicación más enfocada para apoyar presencia retail y activar oportunidades por canal.',
    tags: ['Retail', 'Producto', 'Comunicación', 'Canales'],
    stats: [
      { value: 'SKU', label: 'lectura de producto' },
      { value: '3', label: 'prioridades retail' },
      { value: '+', label: 'visibilidad' },
    ],
  },
  footballLegends: {
    title: 'Football Legends',
    category: 'Marketing Business Development',
    description: 'Desarrollo comercial, marketing y presencia de marca.',
    image: 'assets/images/projects/portfolio/football-legends.png',
    overview:
      'Acompañamos el desarrollo de una propuesta con potencial de marca, comunidad y crecimiento comercial.',
    challenge:
      'Conectar una idea de entretenimiento/deporte con una ruta de marketing y negocio más clara.',
    solution:
      'Ordenamos posicionamiento, mensajes y oportunidades comerciales para construir una presencia más accionable.',
    outcome:
      'Una base de desarrollo comercial que facilita presentar, activar y evolucionar la propuesta.',
    tags: ['Business development', 'Sports marketing', 'Marca', 'Comunidad'],
    stats: [
      { value: 'MKT', label: 'desarrollo comercial' },
      { value: 'Fans', label: 'comunidad objetivo' },
      { value: '1', label: 'propuesta ordenada' },
    ],
  },
  organizerStar: {
    title: 'Organizer Star/HBO',
    category: 'Streaming Website',
    description: 'Experiencia digital orientada a contenido y consumo en streaming.',
    image: 'assets/images/projects/portfolio/organizer-star.png',
    overview:
      'Creamos una experiencia web pensada para presentar contenido, facilitar navegación y sostener una percepción digital más sólida.',
    challenge:
      'Diseñar una presencia digital que organizara contenido y ayudara a comunicar la propuesta con claridad.',
    solution:
      'Planteamos una estructura web enfocada en jerarquía, consumo de información y experiencia de usuario.',
    outcome:
      'Una experiencia digital más ordenada para presentar contenido y facilitar interacción con la marca.',
    tags: ['Website', 'Streaming', 'UX/UI', 'Contenido'],
    stats: [
      { value: 'UX', label: 'estructura web' },
      { value: 'UI', label: 'experiencia visual' },
      { value: '1', label: 'flujo de contenido' },
    ],
  },
  estrellaReal: {
    title: 'Estrella Real',
    category: 'Launching Marketing Strategy',
    description: 'Lanzamiento de marca para generar awareness y activar demanda.',
    image: 'assets/images/projects/portfolio/estrella-real.png',
    overview:
      'Definimos una estrategia de lanzamiento para acompañar la salida de marca con claridad, presencia y dirección comercial.',
    challenge:
      'Construir awareness inicial y convertirlo en una ruta de comunicación útil para activar demanda.',
    solution:
      'Organizamos mensajes, prioridades y etapas de lanzamiento para que la marca pudiera presentarse con fuerza.',
    outcome:
      'Una base de lanzamiento más clara para comunicar identidad, valor y oportunidad comercial.',
    tags: ['Lanzamiento', 'Awareness', 'Marketing', 'Marca'],
    stats: [
      { value: '0-1', label: 'salida a mercado' },
      { value: '3', label: 'etapas de lanzamiento' },
      { value: '+', label: 'awareness' },
    ],
  },
  laTexana: {
    title: 'La Texana',
    category: 'Retail Marketing Strategy',
    description: 'Comunicación, presencia comercial y posicionamiento retail.',
    image: 'assets/images/projects/portfolio/la-texana.png',
    overview:
      'Trabajamos una estrategia retail orientada a mejorar presencia, comunicación y lectura de valor en mercado.',
    challenge:
      'Alinear identidad, producto y puntos de contacto para competir con más claridad en retail.',
    solution:
      'Construimos una ruta de comunicación y posicionamiento enfocada en canal, audiencia y oportunidad comercial.',
    outcome:
      'Una presencia más ordenada para comunicar valor, reforzar percepción y apoyar activaciones comerciales.',
    tags: ['Retail', 'Posicionamiento', 'Canales', 'Comunicación'],
    stats: [
      { value: 'POS', label: 'enfoque retail' },
      { value: '3', label: 'mensajes clave' },
      { value: '+', label: 'presencia comercial' },
    ],
  },
  metaown: {
    title: 'Metaown',
    category: 'App Development e Integración AI',
    description: 'Producto digital con app e integración de inteligencia artificial.',
    image: 'assets/images/projects/portfolio/metaown.png',
    overview:
      'Acompañamos una propuesta digital con enfoque en producto, experiencia e integración de inteligencia artificial.',
    challenge:
      'Convertir una idea tecnológica en una experiencia de producto clara, funcional y con valor percibido.',
    solution:
      'Estructuramos el enfoque de app, experiencia y capacidades AI para apoyar una evolución de producto más sólida.',
    outcome:
      'Una base digital más clara para comunicar, desarrollar y escalar una propuesta tecnológica.',
    tags: ['App development', 'AI', 'Producto digital', 'UX'],
    stats: [
      { value: 'AI', label: 'integración' },
      { value: 'APP', label: 'producto digital' },
      { value: '+', label: 'escalabilidad' },
    ],
  },
  evry: {
    title: 'Evry',
    category: 'Ecommerce Development',
    description: 'Ecommerce para experiencia de compra, operación digital y conversión.',
    image: 'assets/images/projects/portfolio/evry.png',
    overview:
      'Desarrollamos una visión ecommerce centrada en experiencia de compra, claridad de catálogo y operación digital.',
    challenge:
      'Crear una experiencia que facilitara compra, confianza y lectura de producto en un entorno digital.',
    solution:
      'Planteamos estructura, navegación y enfoque de conversión para apoyar la experiencia comercial online.',
    outcome:
      'Una base ecommerce más ordenada para vender, comunicar producto y mejorar experiencia de usuario.',
    tags: ['Ecommerce', 'UX/UI', 'Conversión', 'Operación digital'],
    stats: [
      { value: 'Shop', label: 'experiencia digital' },
      { value: 'UX', label: 'flujo de compra' },
      { value: '+', label: 'conversión' },
    ],
  },
  pixelMining: {
    title: 'Pixel Mining',
    category: 'App Development',
    description: 'Aplicación digital con enfoque en producto, experiencia y escalabilidad.',
    image: 'assets/images/projects/portfolio/pixel-mining.png',
    overview:
      'Trabajamos una propuesta de aplicación con foco en estructura de producto, experiencia de usuario y potencial de crecimiento.',
    challenge:
      'Dar forma a una experiencia digital que fuera entendible, usable y preparada para evolucionar.',
    solution:
      'Definimos una ruta de producto y comunicación para apoyar desarrollo, presentación y escalabilidad.',
    outcome:
      'Una base de app más clara para comunicar valor, ordenar funcionalidades y sostener crecimiento.',
    tags: ['App development', 'Producto', 'UX', 'Escalabilidad'],
    stats: [
      { value: 'APP', label: 'producto digital' },
      { value: 'UX', label: 'experiencia' },
      { value: '+', label: 'evolución' },
    ],
  },
} satisfies Record<string, ClientCaseStudy>;
